export const fmtDate = (d) => {
  // Parse date string as local time to avoid timezone shift
  // Date strings like "2026-01-13" are interpreted as UTC, causing off-by-one errors
  if (typeof d === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(d)) {
    const [year, month, day] = d.split('-').map(Number);
    return new Date(year, month - 1, day).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  }
  return new Date(d).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

export const daysBetween = (a, b) => {
  const A = new Date(a).setHours(0, 0, 0, 0);
  const B = new Date(b).setHours(0, 0, 0, 0);
  return Math.max(1, Math.round((B - A) / (1000 * 60 * 60 * 24)) + 1);
};

export const toGoogleSearch = (q) => `https://www.google.com/search?q=${encodeURIComponent(q)}`;

// Extract city and state from a full address string
// e.g. "100 S W Temple St, Salt Lake City, UT 84101" -> "Salt Lake City, UT"
// Also handles "550 Beachview Drive St. Simons Island, GA 31522" (no comma between street and city)
const extractCityState = (input) => {
  if (!input) return '';
  if (!input.includes(',')) {
    // No commas at all — try to find a state abbreviation in the string
    // e.g. "550 Beachview Drive St Simons Island GA 31522"
    const stateMatch = input.match(/\s([A-Z]{2})\s+\d{5}/);
    if (stateMatch) {
      const state = stateMatch[1];
      const beforeState = input.substring(0, stateMatch.index).trim();
      // Try to extract city — look for "St." or "Saint" or "San" patterns, or take last 2-3 words
      const words = beforeState.split(/\s+/);
      // Heuristic: city is usually the last 2-3 words before the state
      // But street names can be tricky. Try to find common city prefixes
      const cityPrefixes = ['St.', 'St', 'Saint', 'San', 'Santa', 'Fort', 'Ft.', 'New', 'North', 'South', 'East', 'West', 'El', 'Las', 'Los', 'Port', 'Bay'];
      let cityStart = -1;
      for (let i = 0; i < words.length; i++) {
        if (cityPrefixes.includes(words[i])) {
          // "St." in "9th St. Salina" is a street type, not a city prefix (Saint)
          // Skip if it's followed by exactly 1 word and there are 2+ words before it
          if ((words[i] === 'St.' || words[i] === 'St') && i > 1 && words.length === i + 2) {
            continue;
          }
          cityStart = i;
          break;
        }
      }
      let city;
      if (cityStart >= 0) {
        city = words.slice(cityStart).join(' ');
      } else {
        // Take last word as city (more reliable than last 2 which may include street type)
        city = words.slice(-1).join(' ');
      }
      return `${city}, ${state}`;
    }
    return input;
  }
  const parts = input.split(',').map(p => p.trim());
  // If only one comma, could be "City, State" already — but also could be "Street City, GA 31522"
  if (parts.length === 2) {
    const stateMatch = parts[1].match(/^([A-Z]{2})(\s+\d{5})?$/);
    if (stateMatch) {
      // parts[0] is "street city" — need to extract city from it
      const state = stateMatch[1];
      const beforeState = parts[0];
      const words = beforeState.split(/\s+/);
      // Try city prefixes
      const cityPrefixes = ['St.', 'St', 'Saint', 'San', 'Santa', 'Fort', 'Ft.', 'New', 'North', 'South', 'East', 'West', 'El', 'Las', 'Los', 'Port', 'Bay'];
      let cityStart = -1;
      for (let i = 0; i < words.length; i++) {
        if (cityPrefixes.includes(words[i])) {
          // "St." in "9th St. Salina" is a street type, not a city prefix (Saint)
          // Skip if it's followed by exactly 1 word and there are 2+ words before it
          if ((words[i] === 'St.' || words[i] === 'St') && i > 1 && words.length === i + 2) {
            continue;
          }
          cityStart = i;
          break;
        }
      }
      let city;
      if (cityStart >= 0) {
        city = words.slice(cityStart).join(' ');
      } else {
        // Take last word as city (more reliable than last 2 which may include street type)
        city = words.slice(-1).join(' ');
      }
      return `${city}, ${state}`;
    }
    // Not a state pattern, return as-is
    return input.trim();
  }
  // If 3+ parts: street, city, state zip
  // Find the part that looks like a state (2-letter uppercase or known state)
  for (let i = 1; i < parts.length; i++) {
    const part = parts[i].trim();
    // Check if this part is a 2-letter state code or starts with one
    const stateMatch = part.match(/^([A-Z]{2})(\s+\d{5})?$/);
    if (stateMatch) {
      // City is the part before this, state is this part (without zip)
      const city = parts[i - 1].trim();
      const state = stateMatch[1];
      return `${city}, ${state}`;
    }
  }
  // Fallback: take last two meaningful parts
  if (parts.length >= 2) {
    return `${parts[parts.length - 2]}, ${parts[parts.length - 1].split(' ')[0]}`;
  }
  return parts[0];
};

// Extract just the city name from a full address
const extractCity = (input) => {
  const cityState = extractCityState(input);
  if (cityState.includes(',')) {
    return cityState.split(',')[0].trim();
  }
  return cityState;
};

export const toFlights = ({ from, to, start, end }) => {
  // Build Expedia flight search URL with location and dates
  const baseUrl = 'https://www.expedia.com/Flights';
  const params = new URLSearchParams();
  
  // Map common airport codes from city/airport names
  const airportMap = {
    'STL': 'STL', 'St. Louis': 'STL', 'Saint Louis': 'STL',
    'DFW': 'DFW', 'Dallas': 'DFW', 'Fort Worth': 'DFW',
    'ORD': 'ORD', 'Chicago': 'ORD',
    'ATL': 'ATL', 'Atlanta': 'ATL',
    'LAX': 'LAX', 'Los Angeles': 'LAX',
    'JFK': 'JFK', 'New York': 'JFK', 'NYC': 'JFK',
    'MIA': 'MIA', 'Miami': 'MIA',
    'DEN': 'DEN', 'Denver': 'DEN',
    'SEA': 'SEA', 'Seattle': 'SEA',
    'PHX': 'PHX', 'Phoenix': 'PHX',
    'IAH': 'IAH', 'Houston': 'IAH',
    'BNA': 'BNA', 'Nashville': 'BNA',
    'MSP': 'MSP', 'Minneapolis': 'MSP',
    'DTW': 'DTW', 'Detroit': 'DTW',
    'PHL': 'PHL', 'Philadelphia': 'PHL',
    'CLT': 'CLT', 'Charlotte': 'CLT',
    'SFO': 'SFO', 'San Francisco': 'SFO',
    'BOS': 'BOS', 'Boston': 'BOS',
    'LAS': 'LAS', 'Las Vegas': 'LAS',
    'MCO': 'MCO', 'Orlando': 'MCO',
    'SAN': 'SAN', 'San Diego': 'SAN',
    'TPA': 'TPA', 'Tampa': 'TPA',
    'RSW': 'RSW', 'Fort Myers': 'RSW', 'Fort Meyers': 'RSW', 'Ft. Myers': 'RSW',
    'PDX': 'PDX', 'Portland': 'PDX',
    'SLC': 'SLC', 'Salt Lake City': 'SLC',
    'IND': 'IND', 'Indianapolis': 'IND',
    'CLE': 'CLE', 'Cleveland': 'CLE',
    'CMH': 'CMH', 'Columbus': 'CMH',
    'AUS': 'AUS', 'Austin': 'AUS',
    'SAT': 'SAT', 'San Antonio': 'SAT',
    'MCI': 'MCI', 'Kansas City': 'MCI',
    'RDU': 'RDU', 'Raleigh': 'RDU',
    'OKC': 'OKC', 'Oklahoma City': 'OKC',
    'MEM': 'MEM', 'Memphis': 'MEM',
    'JAX': 'JAX', 'Jacksonville': 'JAX',
    'ABQ': 'ABQ', 'Albuquerque': 'ABQ',
    'TUL': 'TUL', 'Tulsa': 'TUL',
    'OMA': 'OMA', 'Omaha': 'OMA',
    'BHM': 'BHM', 'Birmingham': 'BHM',
    'LIT': 'LIT', 'Little Rock': 'LIT',
    'BQK': 'BQK', 'Brunswick': 'BQK', 'St. Simons Island': 'BQK', 'Saint Simons Island': 'BQK',
    'SAV': 'SAV', 'Savannah': 'SAV',
    'SLN': 'SLN', 'Salina': 'SLN',
  };

  // Try to resolve airport code
  const resolveAirport = (input) => {
    if (!input) return null;
    // Check if it's already a 3-letter code
    if (input.length === 3 && input === input.toUpperCase()) return input;
    
    // Check the map for exact city names first
    if (airportMap[input]) return airportMap[input];
    
    // Then check for partial matches, but be more careful
    const inputUpper = input.toUpperCase();
    for (const [key, code] of Object.entries(airportMap)) {
      // Only match if it's a full word or the input is exactly the city name
      if (inputUpper === key.toUpperCase() || 
          (inputUpper.includes(' ' + key.toUpperCase()) && !inputUpper.includes(',')) ||
          (inputUpper.includes(key.toUpperCase() + ' ') && !inputUpper.includes(','))) {
        return code;
      }
    }
    
    // For small towns in Illinois, don't try to find airports - use nearby major cities
    if (inputUpper.includes('IL') || inputUpper.includes('ILLINOIS')) {
      // Map Illinois towns to major airports
      if (inputUpper.includes('CHICAGO')) return 'ORD';
      if (inputUpper.includes('SPRINGFIELD')) return 'SPI';
      if (inputUpper.includes('ST. LOUIS')) return 'STL'; // For East St. Louis
      // For other IL towns, return null to let Expedia handle
      return null;
    }
    
    return input; // Return as-is for Expedia to resolve
  };
  
  // Extract city name from full address for airport matching
  const cityName = extractCity(to);
  const destAirport = resolveAirport(cityName) || resolveAirport(to);
  
  // Format dates as YYYY-MM-DD (Expedia expects this format)
  const fmtDate = (d) => {
    if (!d) return null;
    const date = new Date(d);
    if (isNaN(date.getTime())) return null;
    return date.toISOString().slice(0, 10);
  };
  
  const depDate = fmtDate(start);
  const retDate = fmtDate(end);
  
  // Build the leg1 param in Expedia's expected format: from:CODE,to:CODE,departure:YYYY-MM-DDT,return:YYYY-MM-DDT
  const dest = destAirport || cityName || to;
  if (dest) {
    const legParts = [`to:${dest}`];
    if (depDate) legParts.push(`departure:${depDate}T`);
    if (retDate) legParts.push(`return:${retDate}T`);
    params.append('leg1', legParts.join(','));
  }
  
  params.append('trip', 'roundtrip');
  params.append('passengers', 'adults:1');
  params.append('mode', 'search');
  
  // If we have destination info, use the search URL, otherwise fallback to affiliate
  if (dest || depDate) {
    return `${baseUrl}?${params.toString()}`;
  }
  return "https://www.expedia.com/affiliate/eOxf83M";
};

export const toAirbnb = (city, start, end) => {
  if (!city) return 'https://www.airbnb.com';
  const fmtDate = (d) => {
    if (!d) return null;
    const date = new Date(d);
    if (isNaN(date.getTime())) return null;
    return date.toISOString().slice(0, 10);
  };
  const s = fmtDate(start);
  const e = fmtDate(end);
  const params = new URLSearchParams();
  if (s) params.append('checkin', s);
  if (e) params.append('checkout', e);
  params.append('adults', '1');
  params.append('source', 'explore_search');
  return `https://www.airbnb.com/s/${encodeURIComponent(city)}/homes?${params.toString()}`;
};

export const toExpediaHotels = (city, start, end) => {
  // Using Expedia hotel search with proper URL structure
  if (!city) return 'https://www.expedia.com/Hotel-Search';
  
  // Check the full address first for special mappings
  const fullAddressUpper = city.toUpperCase();
  let destination = city;
  
  // Map small Illinois towns to nearby major cities BEFORE extracting city name
  if (fullAddressUpper.includes('HERRICK') && fullAddressUpper.includes('IL')) {
    destination = 'Springfield, IL'; // Use Springfield for Herrick
  } else if (fullAddressUpper.includes('SPRINGFIELD') && fullAddressUpper.includes('IL')) {
    destination = 'Springfield, IL';
  } else {
    // Extract city, state from full address
    destination = extractCityState(city);
  }
  
  const fmtDate = (d) => {
    if (!d) return null;
    const date = new Date(d);
    if (isNaN(date.getTime())) return null;
    return date.toISOString().slice(0, 10);
  };
  
  const baseUrl = 'https://www.expedia.com/Hotel-Search';
  const params = new URLSearchParams();
  params.append('destination', destination);
  if (start) params.append('startDate', fmtDate(start));
  if (end) params.append('endDate', fmtDate(end));
  params.append('rooms', '1');
  params.append('adults', '1');
  params.append('sort', 'RECOMMENDED');
  
  return `${baseUrl}?${params.toString()}`;
};

export const toExpediaVacationRentals = (city, start, end) => {
  // VRBO vacation rentals - extract city name for better results
  if (!city) return 'https://www.vrbo.com';
  
  // Extract city name from full address
  let destination = extractCity(city);
  
  const baseUrl = 'https://www.vrbo.com';
  const params = new URLSearchParams();
  params.append('destination', destination);
  params.append('checkin', new Date(start).toISOString().slice(0, 10));
  params.append('checkout', new Date(end).toISOString().slice(0, 10));
  params.append('adults', '1');
  
  return `${baseUrl}/vacation-rentals?${params.toString()}`;
};

export const toExpediaCarRental = (city, start, end) => {
  // Expedia car rental search with location
  if (!city) return 'https://www.expedia.com/Cars';
  
  // Map small towns to nearby airports/cities for car rentals
  const fullAddressUpper = city.toUpperCase();
  let location = city;
  
  // Map Illinois small towns to nearby airports
  if (fullAddressUpper.includes('HERRICK') && fullAddressUpper.includes('IL')) {
    location = 'Springfield, IL'; // SPI airport
  } else if (fullAddressUpper.includes('SPRINGFIELD') && fullAddressUpper.includes('IL')) {
    location = 'Springfield, IL';
  } else {
    // Extract city, state from full address
    location = extractCityState(city);
  }
  
  const fmtDate = (d) => {
    if (!d) return null;
    const date = new Date(d);
    if (isNaN(date.getTime())) return null;
    return date.toISOString().slice(0, 10);
  };
  
  const baseUrl = 'https://www.expedia.com/Cars';
  const params = new URLSearchParams();
  params.append('destination', location);
  if (start) params.append('startDate', fmtDate(start));
  if (end) params.append('endDate', fmtDate(end));
  return `${baseUrl}?${params.toString()}`;
};

// Keep for backward compatibility
export const toHotelsDotCom = toExpediaHotels;

export const toExpedia = (city, start, end) => {
  const baseUrl = 'https://www.expedia.com/Hotel-Search';
  const params = new URLSearchParams();
  if (city) params.append('destination', city);
  if (start) params.append('startDate', new Date(start).toISOString().slice(0, 10));
  if (end) params.append('endDate', new Date(end).toISOString().slice(0, 10));
  params.append('rooms', '1');
  params.append('adults', '1');
  params.append('affcid', '2MAEli3');
  return `${baseUrl}?${params.toString()}`;
};

export const toGoogleMapsPlace = (address) => `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
export const toGoogleMapsDirections = (address) => {
  // Ensure address is properly formatted and encoded
  const formattedAddress = address ? address.trim() : '';
  if (!formattedAddress) return 'https://www.google.com/maps';
  return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(formattedAddress)}`;
};
export const embedGoogleMap = (address) => `https://www.google.com/maps?q=${encodeURIComponent(address)}&output=embed`;

// Rideshare deep links with destination pre-filled
export const toUber = (destination, lat, lng) => {
  const params = new URLSearchParams();
  
  if (lat && lng) {
    params.append('action', 'setPickup');
    params.append('dropoff[latitude]', lat);
    params.append('dropoff[longitude]', lng);
    params.append('dropoff[nickname]', destination || 'Event Venue');
  } else if (destination) {
    // No coordinates — use text search for destination
    params.append('action', 'setPickup');
    params.append('dropoff', destination);
  }
  
  // Use Uber mobile web link (works on all devices)
  return params.toString() ? `https://m.uber.com/ul/?${params.toString()}` : 'https://www.uber.com/ride/';
};

export const toLyft = (destination, lat, lng) => {
  // Lyft deep link with destination
  const params = new URLSearchParams();
  
  if (lat && lng) {
    params.append('id', 'lyft');
    params.append('destination[latitude]', lat);
    params.append('destination[longitude]', lng);
  } else if (destination) {
    params.append('id', 'lyft');
    params.append('destination', destination);
  }
  
  return params.toString() ? `https://lyft.com/ride?${params.toString()}` : 'https://www.lyft.com/';
};

export const toParking = (address) => {
  // SpotHero parking search
  if (!address) return 'https://spothero.com';
  return `https://spothero.com/search?latitude=&longitude=&search_string=${encodeURIComponent(address)}`;
};
